import type { ReactNode } from "react";
import { useInRouterContext } from "react-router-dom";
import { LocalDrawerStackProvider } from "./LocalDrawerStackProvider";
import { RoutedDrawerStackProvider } from "./RoutedDrawerStackProvider";
import type { DrawerRouteDefinition, DrawerRouteLevel } from "./types";

interface DrawerStackProviderProps {
  children: ReactNode;
  /** Maximum number of stacked drawers allowed (default: 4) */
  maxDepth?: number;
  /**
   * Drawer kinds that live in the URL. Inside a router, a drawer pushed with a `route` whose
   * segment has a `component` becomes a `{segment}/{id}[/{tab}]` level of the URL, so Back,
   * refresh and shared links restore it. Pass a module-level array: a new array on every
   * render re-parses the URL and re-renders every `useDrawerStack` consumer, and switching
   * between routed and in-memory mode remounts the children.
   */
  routes?: readonly DrawerRouteDefinition[];
  /**
   * Leading path of every page, such as a site prefix (`/acme`), used with `routes`. Levels
   * and global overlay segments are never matched inside it, so a prefix equal to a
   * registered segment is not read as a drawer.
   */
  pathPrefix?: string;
  /**
   * Defer mounting a routed drawer until its ancestors are ready. Deferred drawers remain
   * in the URL and stack, but are not treated as the topmost visible drawer.
   */
  areRouteAncestorsReady?: (ancestors: readonly DrawerRouteLevel[]) => boolean;
}

/**
 * Provides a drawer stack context that enables nested/stacked drawer behavior.
 * Wrap your application (or the relevant subtree) with this provider to enable
 * drawers to open on top of each other with consistent close behavior.
 *
 * Close button: closes topmost drawer only.
 * Backdrop click: closes the entire stack.
 *
 * Without `routes` (or outside a router) the stack is kept in memory.
 */
export function DrawerStackProvider({
  children,
  maxDepth = 4,
  routes,
  pathPrefix,
  areRouteAncestorsReady,
}: DrawerStackProviderProps) {
  const inRouter = useInRouterContext();

  if (routes && inRouter) {
    return (
      <RoutedDrawerStackProvider
        maxDepth={maxDepth}
        routes={routes}
        pathPrefix={pathPrefix}
        areRouteAncestorsReady={areRouteAncestorsReady}
      >
        {children}
      </RoutedDrawerStackProvider>
    );
  }
  return (
    <LocalDrawerStackProvider maxDepth={maxDepth}>
      {children}
    </LocalDrawerStackProvider>
  );
}
